<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useEventListener, useLocalStorage } from '@vueuse/core';
import { onBeforeRouteLeave, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useCsmlFlowGraph } from '../../composables/useCsmlFlowGraph';
import {
  generateCSML,
  nodeHasInvalidApiJson,
} from '../../composables/useCsmlGenerator';
import { alignNodes } from '../../composables/useCsmlAlign';
import { computeAutoLayout } from '../../composables/useCsmlAutoLayout';
import { collectIssues } from '../../composables/useCsmlValidation';
import { useCsmlShortcuts } from '../../composables/useCsmlShortcuts';
import { computeGuides } from '../../composables/useCsmlGuides';
import { getOutPorts } from '../../composables/useCsmlPorts';
import {
  COL_GAP,
  NODE_MIN_HEIGHT,
  NODE_WIDTH,
  snapToGrid,
} from '../../constants/csmlLayout';
import { buildFromScript } from '../../composables/useCsmlScriptImport';
import CsmlConnectionPanel from './CsmlConnectionPanel.vue';
import CsmlOnboardingCard from './CsmlOnboardingCard.vue';
import CsmlConfirmDialog from './CsmlConfirmDialog.vue';
import CsmlShortcutsDialog from './CsmlShortcutsDialog.vue';
import CsmlNodePicker from './CsmlNodePicker.vue';
import CsmlAlignToolbar from './CsmlAlignToolbar.vue';
import CsmlVisualToolbar from './CsmlVisualToolbar.vue';
import CsmlNodePalette from './CsmlNodePalette.vue';
import CsmlFlowCanvas from './CsmlFlowCanvas.vue';
import CsmlPropertiesPanel from './CsmlPropertiesPanel.vue';
import CsmlScriptPreviewPanel from './CsmlScriptPreviewPanel.vue';
import CsmlBotSettingsDialog from './CsmlBotSettingsDialog.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  initialBot: { type: Object, default: () => ({}) },
  initialCsml: { type: String, default: '' },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits(['save', 'cancel']);

const { t } = useI18n();

const graph = useCsmlFlowGraph();
const {
  nodes,
  connections,
  selectedNodeIds,
  selectedConnection,
  selectedNode,
  zoom,
  isPanMode,
  canvasOffsetX,
  canvasOffsetY,
  addNode,
  deleteNodes,
  selectAll,
  copySelection,
  pasteClipboard,
  duplicateNode,
  duplicateNodes,
  addConnection,
  removeConnection,
  updateNode,
  clearCanvas,
  loadGraph,
  selectNode,
  selectNodes,
  selectConnection,
  setZoom,
  undo,
  redo,
  canUndo,
  canRedo,
  resetHistory,
  markSaved,
  getSnapshot,
  isGraphDirty,
  pauseHistory,
  resumeHistory,
} = graph;

const botName = ref('');
const botDescription = ref('');
const botAvatar = ref(null);
const botAvatarUrl = ref('');
const avatarDeleted = ref(false);
const sidePanel = ref(null);
const lastCodeTab = ref('preview');
const initialized = ref(false);
const settingsDialog = ref(null);
const scriptLossy = ref(false);

const title = computed(() =>
  props.mode === 'create'
    ? t('AGENT_BOTS.VISUAL_BUILDER.TITLE_CREATE')
    : t('AGENT_BOTS.VISUAL_BUILDER.TITLE_EDIT')
);

// Bot name, description and avatar live outside the graph, so they get their
// own snapshot to compare against.
const metaSnapshot = () =>
  JSON.stringify([
    botName.value,
    botDescription.value,
    botAvatarUrl.value,
    avatarDeleted.value,
  ]);
const savedMeta = ref('');

const isDirty = computed(
  () => isGraphDirty.value || metaSnapshot() !== savedMeta.value
);

const issues = computed(() =>
  collectIssues(nodes.value, connections.value, { lossy: scriptLossy.value })
);

const csmlPreview = computed(() =>
  generateCSML(nodes.value, connections.value)
);

const canvasRef = ref(null);

const onFocusNode = id => {
  selectNode(id);
  canvasRef.value?.centerOnNode(id);
};

const onTidyUp = () => {
  const positions = computeAutoLayout(
    nodes.value,
    connections.value,
    canvasRef.value?.getNodeHeights()
  );
  Object.entries(positions).forEach(([id, { x, y }]) => {
    updateNode(Number(id), { x, y });
  });
  // Wait a frame so the cards have re-measured before framing the result.
  requestAnimationFrame(() => canvasRef.value?.fitToContent());
};

// The importer and the example only place nodes on a rough grid, so once the
// cards have rendered (and been measured) lay them out properly.
const tidyAfterRender = async ({ fresh = false } = {}) => {
  await nextTick();
  requestAnimationFrame(() => {
    onTidyUp();
    // A freshly opened bot should start with an empty undo stack, not one
    // that can step back into the importer's rough placement.
    if (fresh) resetHistory();
  });
};

const applyInitialState = () => {
  botName.value = props.initialBot?.name || '';
  botDescription.value = props.initialBot?.description || '';
  botAvatarUrl.value = props.initialBot?.thumbnail || '';
  botAvatar.value = null;
  nextTick(() => {
    savedMeta.value = metaSnapshot();
  });

  const script =
    props.initialCsml || props.initialBot?.bot_config?.csml_content;
  if (script && String(script).trim()) {
    const parsed = buildFromScript(script);
    scriptLossy.value = !!parsed?.lossy;
    if (parsed) {
      loadGraph(parsed);
      tidyAfterRender({ fresh: true });
      return;
    }
  }
  if (!nodes.value.length) {
    addNode('start', 80, 80);
    resetHistory();
  }
};

watch(
  () => props.initialCsml,
  () => {
    if (props.mode === 'edit' && props.initialCsml) {
      applyInitialState();
    }
  }
);

watch(
  () => props.initialBot?.id,
  () => {
    if (props.mode === 'edit' && props.initialBot?.id) {
      applyInitialState();
    }
  }
);

const onDropNode = ({ type, x, y }) => {
  addNode(type, x, y);
};

// Nodes snap to the grid while dragging; holding Alt drags freely.
const onSelectNode = ({ id, additive }) => {
  selectNode(id, additive);
};

const onSelectNodes = ({ ids, additive }) => {
  selectNodes(ids, additive);
};

// Delete/duplicate on a card that belongs to a multi-selection act on the
// whole group; on any other card, just that card.
const groupFor = id =>
  selectedNodeIds.value.length > 1 && selectedNodeIds.value.includes(id)
    ? [...selectedNodeIds.value]
    : [id];

const onDeleteNode = id => {
  deleteNodes(groupFor(id));
};

const onDuplicateNode = id => {
  const ids = groupFor(id);
  if (ids.length > 1) duplicateNodes(ids);
  else duplicateNode(id);
};

const guides = ref(null);
const wireStyle = useLocalStorage('csml-builder-wire-style', 'bezier');

const onNodeDrag = ({ id, x, y, free }) => {
  pauseHistory();
  const group = groupFor(id);
  const dragged = nodes.value.find(node => node.id === id);
  let next = { x, y };
  if (free) {
    guides.value = null;
  } else {
    // Lining up with a neighbour beats the grid when one is within reach.
    const found = computeGuides(nodes.value, group, x, y);
    guides.value = found;
    next = { x: found.x ?? snapToGrid(x), y: found.y ?? snapToGrid(y) };
  }
  const dx = next.x - dragged.x;
  const dy = next.y - dragged.y;
  group.forEach(nodeId => {
    const node = nodes.value.find(n => n.id === nodeId);
    if (node) updateNode(nodeId, { x: node.x + dx, y: node.y + dy });
  });
};

const onNodeDragEnd = () => {
  guides.value = null;
  resumeHistory();
};

const onPortConnect = ({ fromId, toId, fromPortIdx }) => {
  const conn = addConnection(fromId, toId, fromPortIdx);
  // The new wire is its own confirmation; only a silent replacement of an
  // existing wire from that port is worth a word.
  if (conn?.replaced) {
    useAlert(t('AGENT_BOTS.VISUAL_BUILDER.TOAST.CONNECTION_REPLACED'));
  }
};

// Drops a node one column to the right of `id`, level with it, and wires it
// to the first branch that does not lead anywhere yet.
const addAfter = (id, type) => {
  const source = nodes.value.find(node => node.id === id);
  if (!source) return;
  const wired = connections.value
    .filter(conn => conn.fromId === id)
    .map(conn => conn.fromPortIdx ?? 0);
  const port = getOutPorts(source).find(p => !wired.includes(p.idx));
  const next = addNode(
    type,
    snapToGrid(source.x + NODE_WIDTH + COL_GAP),
    source.y
  );
  if (!next) return;
  if (port) addConnection(id, next.id, port.idx);
  selectNode(next.id);
};

const onAddNext = id => addAfter(id, 'say');

// Clicking a palette tile adds the node after the selected card, or in the
// middle of the visible canvas when nothing is selected.
const onPaletteAdd = type => {
  if (selectedNode.value) {
    addAfter(selectedNode.value.id, type);
    return;
  }
  const centre = canvasRef.value?.getViewportCenter() || { x: 0, y: 0 };
  const next = addNode(
    type,
    snapToGrid(centre.x - NODE_WIDTH / 2),
    snapToGrid(centre.y - NODE_MIN_HEIGHT / 2)
  );
  if (next) selectNode(next.id);
};

// The "+" on an unwired port opens a picker; the node it creates lands one
// column to the right, level with that port, and is wired straight to it.
const picker = ref(null);

const onOpenPicker = ({ nodeId, portIdx, clientX, clientY }) => {
  picker.value = { nodeId, portIdx, x: clientX + 12, y: clientY - 16 };
};

const onPickNode = type => {
  const { nodeId, portIdx } = picker.value;
  picker.value = null;
  const source = nodes.value.find(node => node.id === nodeId);
  if (!source) return;
  const port = canvasRef.value
    ?.getPortOffsets()
    [nodeId]?.out.find(p => p.idx === portIdx);
  const next = addNode(
    type,
    snapToGrid(source.x + NODE_WIDTH + COL_GAP),
    snapToGrid(
      source.y + (port?.y ?? NODE_MIN_HEIGHT / 2) - NODE_MIN_HEIGHT / 2
    )
  );
  if (!next) return;
  addConnection(nodeId, next.id, portIdx);
  selectNode(next.id);
};

const onSelectConnection = conn => {
  selectConnection(conn);
};

const onClearSelection = () => {
  selectNode(null);
  selectConnection(null);
};

const onRemoveConnection = ({ fromId, toId, fromPortIdx }) => {
  removeConnection(fromId, toId, fromPortIdx);
  useAlert(t('AGENT_BOTS.VISUAL_BUILDER.TOAST.CONNECTION_REMOVED'));
};

const onDeleteSelectedConnection = () => {
  if (!selectedConnection.value) return;
  onRemoveConnection(selectedConnection.value);
};

// Reconnect keeps the existing wire until a new target is chosen (or the
// gesture is cancelled). Completing a wire from the same port replaces it via
// addConnection; cancelling leaves the original intact.
const onReconnectSelected = () => {
  const conn = selectedConnection.value;
  if (!conn) return;
  canvasRef.value?.startConnecting(conn.fromId, conn.fromPortIdx ?? 0);
};

const onPropertyUpdate = ({ id, patch }) => {
  updateNode(id, patch);
};

const onAlign = action => {
  const positions = alignNodes(
    nodes.value,
    selectedNodeIds.value,
    action,
    canvasRef.value?.getNodeHeights()
  );
  Object.entries(positions).forEach(([id, { x, y }]) => {
    updateNode(Number(id), { x, y });
  });
};

const clearDialog = ref(null);

const onClear = () => clearDialog.value?.open();

const doClear = () => {
  clearCanvas();
  scriptLossy.value = false;
  addNode('start', 80, 80);
  useAlert(t('AGENT_BOTS.VISUAL_BUILDER.TOAST.CLEARED'));
};

const onExample = () => {
  clearCanvas();
  scriptLossy.value = false;
  const start = addNode('start');
  updateNode(start.id, { content: 'Welcome! How can I help?' });
  const api = addNode('api');
  updateNode(api.id, {
    apiUrl: 'https://api.example.com',
    apiMethod: 'GET',
    content: 'API status: {{response.get_info().status}}',
    responseRules: [
      { label: 'Success', field: 'status', operator: 'equals', value: '200' },
    ],
  });
  const assignTeam = addNode('assign');
  updateNode(assignTeam.id, {
    assignType: 'team',
    teamId: '',
  });
  const assignAgent = addNode('assign');
  updateNode(assignAgent.id, {
    assignType: 'agent',
    agentId: '',
  });
  const end = addNode('say');
  updateNode(end.id, { content: 'Thank you!' });
  addConnection(start.id, api.id);
  addConnection(api.id, assignTeam.id, 0);
  addConnection(assignTeam.id, end.id);
  addConnection(api.id, assignAgent.id, 1);
  addConnection(assignAgent.id, end.id);
  tidyAfterRender();
  useAlert(t('AGENT_BOTS.VISUAL_BUILDER.TOAST.EXAMPLE_LOADED'));
};

const onCopy = async () => {
  await navigator.clipboard.writeText(csmlPreview.value);
  useAlert(t('AGENT_BOTS.VISUAL_BUILDER.TOAST.COPIED'));
};

const onExport = () => {
  const blob = new Blob([csmlPreview.value], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `bot_${Date.now()}.csml`;
  a.click();
  URL.revokeObjectURL(a.href);
  useAlert(t('AGENT_BOTS.VISUAL_BUILDER.TOAST.EXPORTED'));
};

const onToggleCode = () => {
  sidePanel.value = sidePanel.value ? null : lastCodeTab.value;
};

watch(sidePanel, value => {
  if (value) lastCodeTab.value = value;
});

// Side panels slide in from the inline-end edge; reduced-motion users just
// get the swap.
const panelTransition = {
  enterActiveClass:
    'transition duration-150 ease-out motion-reduce:transition-none',
  enterFromClass: 'opacity-0 ltr:translate-x-2 rtl:-translate-x-2',
  leaveActiveClass:
    'transition duration-100 ease-in motion-reduce:transition-none',
  leaveToClass: 'opacity-0',
};

const scriptError = ref('');

// A brand-new flow is just a Start card; offer ways to begin until the user
// picks one or adds anything.
const onboardingDismissed = ref(false);
const showOnboarding = computed(
  () =>
    !onboardingDismissed.value &&
    nodes.value.length <= 1 &&
    connections.value.length === 0
);

const onOnboardingPaste = () => {
  onboardingDismissed.value = true;
  sidePanel.value = 'script';
};
const onOnboardingExample = () => {
  onboardingDismissed.value = true;
  onExample();
};

const onBuildFromScript = script => {
  scriptError.value = '';
  if (!script || !String(script).trim()) {
    scriptError.value = t('AGENT_BOTS.VISUAL_BUILDER.TOAST.SCRIPT_EMPTY');
    return;
  }
  if (script === csmlPreview.value) return;
  try {
    const parsed = buildFromScript(script);
    if (!parsed) {
      scriptError.value = t('AGENT_BOTS.VISUAL_BUILDER.TOAST.BUILD_ERROR');
      return;
    }
    scriptLossy.value = !!parsed.lossy;
    loadGraph(parsed);
    tidyAfterRender();
    useAlert(
      t('AGENT_BOTS.VISUAL_BUILDER.TOAST.BUILT', {
        nodes: parsed.nodes.length,
        connections: parsed.connections.length,
      })
    );
  } catch (_) {
    scriptError.value = t('AGENT_BOTS.VISUAL_BUILDER.TOAST.BUILD_ERROR');
  }
};

const handleImageUpload = ({ file, url }) => {
  botAvatar.value = file;
  avatarDeleted.value = false;
  botAvatarUrl.value = url;
};

const handleAvatarDelete = () => {
  botAvatar.value = null;
  botAvatarUrl.value = '';
  avatarDeleted.value = true;
};

const overwriteDialog = ref(null);

// Snapshots of what was submitted so markBotSaved can tell whether the live
// builder still matches when the request completes.
let pendingGraphSnapshot = null;
let pendingMetaSnapshot = null;

const performSave = () => {
  // Saving is still allowed with open issues - they only make the bot less
  // useful - but say so rather than letting them slip by.
  if (issues.value.length) {
    useAlert(
      t('AGENT_BOTS.VISUAL_BUILDER.VALIDATION.SAVE_WITH_ISSUES', {
        count: issues.value.length,
      })
    );
  }
  pendingGraphSnapshot = getSnapshot();
  pendingMetaSnapshot = metaSnapshot();
  const csmlContent = generateCSML(nodes.value, connections.value);
  emit('save', {
    name: botName.value.trim(),
    description: botDescription.value,
    avatar: botAvatar.value,
    removeAvatar: avatarDeleted.value,
    bot_type: 'csml',
    bot_config: {
      csml_content: csmlContent,
    },
  });
};

const onSave = () => {
  if (!botName.value.trim()) {
    useAlert(t('AGENT_BOTS.VISUAL_BUILDER.TOAST.NAME_REQUIRED'));
    return;
  }
  if (scriptLossy.value) {
    useAlert(t('AGENT_BOTS.VISUAL_BUILDER.TOAST.LOSSY_SAVE'));
    return;
  }
  if (!nodes.value.some(n => n.type === 'start')) {
    useAlert(t('AGENT_BOTS.VISUAL_BUILDER.TOAST.START_REQUIRED'));
    return;
  }
  if (nodes.value.some(nodeHasInvalidApiJson)) {
    useAlert(t('AGENT_BOTS.VISUAL_BUILDER.TOAST.API_JSON_INVALID'));
    return;
  }
  if (props.mode === 'edit' && props.initialCsml.trim()) {
    overwriteDialog.value?.open();
    return;
  }
  performSave();
};

const setPanMode = value => {
  isPanMode.value = value;
};
const setOffsetX = value => {
  canvasOffsetX.value = value;
};
const setOffsetY = value => {
  canvasOffsetY.value = value;
};

const router = useRouter();
const leaveDialog = ref(null);
let allowLeave = false;
let pendingLeave = null;

// Leaving with unsaved changes asks first - for in-app navigation here, and
// for closing or reloading the tab below.
onBeforeRouteLeave(to => {
  if (allowLeave || !isDirty.value) return true;
  pendingLeave = to;
  leaveDialog.value?.open();
  return false;
});

const onConfirmLeave = () => {
  allowLeave = true;
  if (pendingLeave) router.push(pendingLeave.fullPath);
};

useEventListener(window, 'beforeunload', event => {
  if (allowLeave || !isDirty.value) return;
  event.preventDefault();
  // eslint-disable-next-line no-param-reassign
  event.returnValue = '';
});

// Called by the parent once the save has actually gone through.
// Keep the submitted snapshots as the saved baseline. Only clear the leave
// guard when the live graph and metadata still match what was submitted —
// otherwise stay dirty so goBack cannot bypass the unsaved-changes dialog.
const markBotSaved = () => {
  const submittedGraph = pendingGraphSnapshot ?? getSnapshot();
  const submittedMeta = pendingMetaSnapshot ?? metaSnapshot();
  const stillMatches =
    getSnapshot() === submittedGraph && metaSnapshot() === submittedMeta;

  markSaved(submittedGraph);
  savedMeta.value = submittedMeta;
  pendingGraphSnapshot = null;
  pendingMetaSnapshot = null;

  if (stillMatches) allowLeave = true;
};
defineExpose({ markSaved: markBotSaved });

const shortcutsDialog = ref(null);
let panModeBeforeSpace = false;

useCsmlShortcuts({
  escape: () => {
    if (canvasRef.value?.cancelConnecting()) return;
    if (picker.value) {
      picker.value = null;
      return;
    }
    onClearSelection();
  },
  remove: () => {
    if (selectedNodeIds.value.length) {
      deleteNodes([...selectedNodeIds.value]);
    } else {
      onDeleteSelectedConnection();
    }
  },
  undo,
  redo,
  duplicate: () => {
    if (selectedNodeIds.value.length) onDuplicateNode(selectedNodeIds.value[0]);
  },
  copy: copySelection,
  paste: pasteClipboard,
  selectAll,
  save: onSave,
  fit: () => canvasRef.value?.fitToContent(),
  resetZoom: () => canvasRef.value?.resetZoom(),
  help: () => shortcutsDialog.value?.open(),
  panStart: () => {
    panModeBeforeSpace = isPanMode.value;
    isPanMode.value = true;
  },
  panEnd: () => {
    isPanMode.value = panModeBeforeSpace;
  },
});

onMounted(() => {
  if (!initialized.value) {
    applyInitialState();
    initialized.value = true;
    // A new bot has no name yet, so ask for it up front.
    if (props.mode === 'create') settingsDialog.value?.open();
  }
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-alpha-1">
    <CsmlVisualToolbar
      v-model:bot-name="botName"
      :title="title"
      :avatar-url="botAvatarUrl"
      :is-saving="isSaving"
      :is-pan-mode="isPanMode"
      :can-undo="canUndo"
      :can-redo="canRedo"
      :issues="issues"
      :is-dirty="isDirty"
      :show-code="!!sidePanel"
      @focus-node="onFocusNode"
      @update:is-pan-mode="setPanMode"
      @back="emit('cancel')"
      @open-settings="settingsDialog?.open()"
      @example="onExample"
      @tidy="onTidyUp"
      @clear="onClear"
      @toggle-code="onToggleCode"
      @undo="undo"
      @help="shortcutsDialog?.open()"
      @redo="redo"
      @export="onExport"
      @copy="onCopy"
      @save="onSave"
    />
    <div class="relative flex-1 min-h-0">
      <CsmlNodePalette @add="onPaletteAdd" />
      <CsmlFlowCanvas
        ref="canvasRef"
        class="absolute inset-0"
        :nodes="nodes"
        :connections="connections"
        :selected-node-ids="selectedNodeIds"
        :selected-connection="selectedConnection"
        :zoom="zoom"
        :is-pan-mode="isPanMode"
        :offset-x="canvasOffsetX"
        :offset-y="canvasOffsetY"
        :guides="guides"
        :wire-style="wireStyle"
        @drop-node="onDropNode"
        @select-node="onSelectNode"
        @select-nodes="onSelectNodes"
        @select-connection="onSelectConnection"
        @remove-connection="onRemoveConnection"
        @clear-selection="onClearSelection"
        @node-drag="onNodeDrag"
        @node-drag-end="onNodeDragEnd"
        @delete-node="onDeleteNode"
        @duplicate-node="onDuplicateNode"
        @add-next="onAddNext"
        @add-at-port="onOpenPicker"
        @port-click="onPortConnect"
        @update:zoom="setZoom"
        @update:wire-style="wireStyle = $event"
        @tidy="onTidyUp"
        @update:offset-x="setOffsetX"
        @update:offset-y="setOffsetY"
      />
      <div
        v-if="showOnboarding"
        class="absolute inset-x-0 z-20 flex justify-center pointer-events-none bottom-20"
      >
        <CsmlOnboardingCard
          @scratch="onboardingDismissed = true"
          @example="onOnboardingExample"
          @paste="onOnboardingPaste"
        />
      </div>
      <CsmlAlignToolbar
        v-if="selectedNodeIds.length > 1"
        :count="selectedNodeIds.length"
        @align="onAlign"
      />
      <div
        class="absolute z-30 flex items-start gap-3 pointer-events-none top-3 bottom-3 end-3"
      >
        <Transition v-bind="panelTransition">
          <CsmlScriptPreviewPanel
            v-if="sidePanel"
            v-model:mode="sidePanel"
            :preview-content="csmlPreview"
            :error="scriptError"
            @build="onBuildFromScript"
            @copy="onCopy"
            @close="sidePanel = null"
          />
        </Transition>
        <Transition v-bind="panelTransition">
          <CsmlConnectionPanel
            v-if="selectedConnection"
            :connection="selectedConnection"
            :nodes="nodes"
            @reconnect="onReconnectSelected"
            @delete="onDeleteSelectedConnection"
            @close="onClearSelection"
          />
        </Transition>
        <Transition v-bind="panelTransition">
          <CsmlPropertiesPanel
            v-if="selectedNode"
            :node="selectedNode"
            @update="onPropertyUpdate"
            @duplicate="onDuplicateNode"
            @delete="onDeleteNode"
            @close="onClearSelection"
          />
        </Transition>
      </div>
    </div>
    <CsmlConfirmDialog
      ref="clearDialog"
      :title="t('AGENT_BOTS.VISUAL_BUILDER.CLEAR')"
      :description="t('AGENT_BOTS.VISUAL_BUILDER.CLEAR_CONFIRM')"
      :confirm-label="t('AGENT_BOTS.VISUAL_BUILDER.CLEAR')"
      @confirm="doClear"
    />
    <CsmlConfirmDialog
      ref="overwriteDialog"
      type="edit"
      :title="t('AGENT_BOTS.VISUAL_BUILDER.SAVE')"
      :description="t('AGENT_BOTS.VISUAL_BUILDER.TOAST.SAVE_OVERWRITE_CONFIRM')"
      :confirm-label="t('AGENT_BOTS.VISUAL_BUILDER.SAVE')"
      @confirm="performSave"
    />
    <CsmlConfirmDialog
      ref="leaveDialog"
      :title="t('AGENT_BOTS.VISUAL_BUILDER.LEAVE.TITLE')"
      :description="t('AGENT_BOTS.VISUAL_BUILDER.LEAVE.DESCRIPTION')"
      :confirm-label="t('AGENT_BOTS.VISUAL_BUILDER.LEAVE.CONFIRM')"
      @confirm="onConfirmLeave"
    />
    <CsmlShortcutsDialog ref="shortcutsDialog" />
    <CsmlNodePicker
      v-if="picker"
      :x="picker.x"
      :y="picker.y"
      @pick="onPickNode"
      @close="picker = null"
    />
    <CsmlBotSettingsDialog
      ref="settingsDialog"
      v-model:name="botName"
      v-model:description="botDescription"
      :avatar-url="botAvatarUrl"
      @upload="handleImageUpload"
      @delete-avatar="handleAvatarDelete"
    />
  </div>
</template>
